import React, { useEffect, useRef, useState, memo } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import * as THREE from 'three';

// ✅ Preload the model early
useGLTF.preload('/models/laptop.glb');

const LaptopModel = ({ onPartClick, onHover, selectedPart, onLoaded }) => {
  const group = useRef();
  const { scene, animations } = useGLTF('/models/laptop.compressed.glb');
  const { actions } = useAnimations(animations, group);
  const [hasOpened, setHasOpened] = useState(false);
  const [highlightedMesh, setHighlightedMesh] = useState(null);

  // ✅ Notify parent that model is ready
  useEffect(() => {
    if (scene && onLoaded) {
      onLoaded();
    }
  }, [scene, onLoaded]);

  // ✅ Play opening animation once
  useEffect(() => {
    if (!hasOpened && animations.length > 0 && actions && actions[animations[0].name]) {
      const openAction = actions[animations[0].name];
      openAction.reset().setLoop(THREE.LoopOnce).play();
      openAction.clampWhenFinished = true;
      setHasOpened(true);
    }
  }, [animations, actions, hasOpened]);

  // ✅ Cache original materials once
  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh && !child.userData.originalMaterial) {
        child.userData.originalMaterial = child.material.clone();
        child.frustumCulled = true; // Optional: Culls offscreen meshes
        child.castShadow = false;   // Optional: Avoid extra computation
        child.receiveShadow = false;
      }
    });
  }, [scene]);

  // ✅ Highlight selected part
  useEffect(() => {
    if (!selectedPart) return;

    // Reset previous highlight
    if (highlightedMesh && highlightedMesh.userData.originalMaterial) {
      highlightedMesh.material = highlightedMesh.userData.originalMaterial;
    }

    let matchedMesh = null;

    scene.traverse((child) => {
      if (child.isMesh) {
        const alias = partNameAliases[child.name] || child.name;
        if (alias === selectedPart) {
          matchedMesh = child;
        }
      }
    });

    // Highlight the matched mesh
    if (matchedMesh) {
      const highlightMaterial = new THREE.MeshStandardMaterial({ color: 0xffff00 });
      matchedMesh.material = highlightMaterial;
      setHighlightedMesh(matchedMesh);
    }
  }, [selectedPart, scene]);

  const handleClick = (e) => {
    e.stopPropagation();
    onPartClick?.(e.object.name);
  };

  const handlePointerOver = (e) => {
    document.body.style.cursor = 'pointer';
    onHover?.(e.object.name);
  };

  const handlePointerOut = () => {
    document.body.style.cursor = 'default';
    onHover?.(null);
  };

  return (
    <primitive
      ref={group}
      object={scene}
      dispose={null}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    />
  );
};

const partNameAliases = {
  Object_4: 'Keyboard',
  Object_15: 'Secondary Display Screen',
  Object_22: 'Main Display Screen',
  Object_42: 'Touchpad',
  Object_33: 'Overheating & Fan',
  Object_72: 'Overheating & Fan',
  Object_57: 'Overheating & Fan',
  Object_38: 'Battery',
  Object_29: 'Camera & Webcam',
  Object_67: 'Ports & Charging',
  Object_68: 'Ports & Charging',
  Object_35: 'Ports & Charging',
  Object_63: 'Audio',
};

// ✅ Prevent re-render unless props change
export default memo(LaptopModel);
